import React from "react";
import { motion } from "framer-motion";
import { ProductStatus } from "./ProductStatus";
import { FeatureList } from "./FeatureList";
import {
  BASIC_TIER,
  ENTERPRISE_TIER,
  PRO_TIER,
  ProductCardProps,
  ProductTier,
} from "./types";
import { ActionButton } from "./ActionButton";
import { BotIcon, CloudUploadIcon, CodeIcon } from "lucide-react";

const getTierBgColor = (tier: ProductTier | undefined) => {
  if (!tier) return "bg-gray-100 text-gray-600";
  if (tier === ENTERPRISE_TIER) return "bg-purple-100 text-purple-600";
  if (tier === BASIC_TIER) return "bg-green-100 text-green-600";
  if (tier === PRO_TIER) return "bg-sky-500 text-white";
};

export const ProductCard: React.FC<ProductCardProps> = ({
  config,
  status,
  onAction,
  variants,
  isUpdatePlan,
  planUpdateAction,
}) => {
  const IconComponent = config.icon;
  const buttonText =
    !status.isPurchased && status.isEnabled ? "Go to Product" : "View Details";
  const iconColor = config.iconColor;

  const getIcon = () => {
    switch (iconColor) {
      case "blue":
        return (
          <div className={`p-2 bg-green-100 rounded-lg`}>
            <BotIcon className="w-6 h-6 text-green-600" />
          </div>
        );
      case "purple":
        return (
          <div className={`p-2 bg-purple-100 rounded-lg`}>
            <CodeIcon className="w-6 h-6 text-purple-600" />
          </div>
        );
      default:
        return null;
    }
  };

  const actionButtonStatus = (status: any) => {
    if (status.isPurchased) {
      return "purchased";
    }
    if (!status.isPurchased && status.isEnabled) {
      return "enabled";
    }
    if (status.isExipred) return "expired";

    return "disabled";
  };
  return (
    <motion.div
      variants={variants}
      className="relative bg-white rounded-2xl shadow-xl overflow-hidden border border-gray-100"
    >
      <div className="p-8">
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-3">
            {getIcon()}
            <h2 className="text-2xl font-bold text-gray-900">{config.name}</h2>
            {/* Product tier Badge */}
            {status.paidDetails?.tier && (
              <div
                className={`text-xs font-medium px-2 py-1 rounded-full ${getTierBgColor(status.paidDetails?.tier as ProductTier)}`}
              >
                {status.paidDetails?.tier}
              </div>
            )}
          </div>

          <ProductStatus status={status} />
        </div>

        <div className="space-y-4 mb-8">
          <p className="text-gray-600 leading-relaxed">{config.description}</p>
          <FeatureList features={config.features} />
        </div>
        <div className="flex flex-row gap-2 justify-between mb-2">
          {isUpdatePlan && (
            <button
              onClick={planUpdateAction}
              className="w-full py-3 mr-4 px-6 bg-gradient-to-r from-indigo-800 to-sky-600 text-white rounded-lg font-medium flex items-center justify-center gap-3 transition-all duration-200 shadow-lg hover:shadow-xl group"
            >
              <CloudUploadIcon className="w-5 h-5" />
              <span>Update Plan</span>
            </button>
          )}

          <ActionButton
            onClick={onAction}
            text={buttonText}
            status={actionButtonStatus(status)}
          />
        </div>
      </div>
    </motion.div>
  );
};
